<template>
	<!-- Listing / category page (numeric segment: /blog/2, /blog/3, … or a category slug: /blog/category-slug) -->
	<div v-if="isListingPage" class="news-page">
		<Head>
			<Title>{{ ensureBrandSuffix(listingTitle) }}</Title>
			<Meta v-if="config.public.isIndex" name="robots" content="index, follow" />
			<Meta v-else name="robots" content="noindex, nofollow" />
			<Meta name="description" :content="$t('pages.news.meta_description')" />
			<Meta property="og:title" :content="ensureBrandSuffix(listingTitle)" />
			<Meta property="og:description" :content="$t('pages.news.meta_description')" />
			<Meta name="twitter:title" :content="ensureBrandSuffix(listingTitle)" />
			<Meta name="twitter:description" :content="$t('pages.news.meta_description')" />
			<Link rel="canonical" :href="`${url.origin}${url.pathname}`" />
		</Head>
		<NewsPageList />
	</div>

	<!-- Post page (/blog/post-slug) -->
	<div v-else class="news-post">
		<Head>
			<Title>{{ ensureBrandSuffix(metaTitle) }}</Title>
			<Meta name="description" :content="metaDescription" />
			<Meta property="og:title" :content="ensureBrandSuffix(metaTitle)" />
			<Meta property="og:description" :content="metaDescription" />
			<Meta name="twitter:title" :content="ensureBrandSuffix(metaTitle)" />
			<Meta name="twitter:description" :content="metaDescription" />
			<Meta v-if="config.public.isIndex" name="robots" :content="robotsCalculated" />
			<Meta v-else name="robots" content="noindex, nofollow" />
			<Link
				rel="canonical"
				:href="newsForm.metaCanonical && newsForm.metaCanonical?.trim() !== '' ? newsForm.metaCanonical : `${url.origin}${url.pathname}`" />
		</Head>

		<GlobalHeader />
		<NewsPageHero titleTag="p" />
		<div class="news-post__content-wrapper">
			<div class="news-post__container">
				<GlobalBreadcrumbs :items="breadcrumbsItems" />
				<div class="news-post__content">
					<div class="news-post__toc">
						<NewsToc v-if="tocItems.length" :items="tocItems" />
					</div>
					<div class="news-post__post">
						<NewsContent :newsForm="newsForm" />
						<ButtonPrimary :to="localePath('blog')" class="news-post__back-btn" iconBefore="arrow-left">
							{{ $t('common.back') }}
						</ButtonPrimary>
					</div>
					<div class="news-post__latest-news">
						<NewsCard v-for="post in computedLatestNews" :key="post.id" :post="post" card />
					</div>
					<div class="news-post__latest-news-mobile">
						<Swiper
							class="news-post__swiper"
							:slidesPerView="'auto'"
							:space-between="16"
							@after-init="
								(swiper) => {
									useTimeoutFn(() => {
										swiper.slideNext(0)
										swiper.slidePrev(0)
									}, 400)
								}
							">
							<SwiperSlide v-for="post in computedLatestNews" :key="post.id" class="news-post__swiper-slide">
								<NewsCard :key="post.id" :post="post" card />
							</SwiperSlide>
						</Swiper>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
const newsStore = useNewsStore()
const $t = useI18n().t
const latestNews = ref<Partial<INewsModel>[]>([])
const tocItems = ref<INewsTocItem[]>([])
const i18nService = usei18nService()
const locationRedirectStore = useLocationRedirectStore()
const localeLang = i18nService.getLocaleLang()
const today = new Date()
const route = useRoute()
const localePath = useLocalePath()
const url = useRequestURL()
const config = useNuxtApp().$config
const { storageUrl } = useStorageService()

const segment = route.params.newsTitle?.[0] as string
const pageContext = newsStore.state.pageContext
const isListingPage = pageContext?.type !== 'post'

const listingPage = isListingPage ? (pageContext?.page ?? 1) : 1

const listingTitle = computed(() => {
	const base = pageContext?.type === 'category' && pageContext.category ? pageContext.category.name : $t('pages.news.meta_title')

	return listingPage > 1 ? `${base} - ${$t('common.page')} ${listingPage}` : base
})

// --- Post page state ---
const newsForm = reactive<Record<string, any>>({
	date: today,
	metaCanonical: '',
	metaTitle: '',
	metaDescription: '',
	metaRobots: [],
	language: 'PL',
	title: '',
	content: '',
	topImage: undefined,
	bottomImage: undefined,
	categories: [],
	author: null,
})

if (pageContext?.type === 'post') {
	const post = pageContext.post

	if (!post) {
		navigateTo(localePath('/'))
	} else {
		newsForm.date = new Date(post.publishedAt ?? post.createdAt)
		newsForm.metaCanonical = post.metaCanonical
		newsForm.metaTitle = post.metaTitle
		newsForm.metaDescription = post.metaDescription
		newsForm.metaRobots = post.metaRobots
		newsForm.language = post.language
		newsForm.title = post.title
		if (typeof post.content === 'string') {
			const toc = buildNewsToc(post.content)
			newsForm.content = toc.content
			tocItems.value = toc.items
		} else {
			newsForm.content = post.content
		}
		newsForm.topImage = post.files?.find((f) => f.collection === EFileCollection.NEWS_TOP_IMAGE)
		newsForm.bottomImage = post.files?.find((f) => f.collection === EFileCollection.NEWS_BOTTOM_IMAGE)
		newsForm.categories = post.categories ?? []
		newsForm.author = post.author ?? null

		latestNews.value = await newsStore.getLatestNews()
	}
}

watch(
	() => locationRedirectStore.getRedirectLang,
	(lang) => {
		if (!lang) return
		const post = pageContext?.type === 'post' ? pageContext.post : null
		const translatedSlug = post?.translations?.find((t) => t.language.toLowerCase() === lang)?.slug
		i18nService.changeLang(lang, translatedSlug)
	},
)

const metaTitle = computed(() => newsForm.metaTitle?.trim() || newsForm.title)
const metaDescription = computed(() => newsForm.metaDescription?.trim() || metaTitle.value)

// schema.org Person for the post author - every field except id is optional, so only present ones are rendered
const buildAuthorJsonld = (author: IAuthorModel | null, lang: string) => {
	if (!author) return null

	const name = authorFullName(author).trim()
	const givenName = author.givenName?.trim()
	const familyName = author.familyName?.trim()
	const jobTitle = author.jobTitle?.[lang]?.trim()
	const description = author.description?.[lang]?.trim()
	const worksFor = author.worksFor?.trim()
	const sameAs = (author.sameAs ?? []).map((link) => link.trim()).filter(Boolean)

	// a Person with nothing but its type is useless, fall back to the organization then
	if (!name && !jobTitle && !description && !worksFor && !sameAs.length) return null

	return {
		'@type': 'Person' as const,
		...(name && { name }),
		...(givenName && { givenName }),
		...(familyName && { familyName }),
		...(jobTitle && { jobTitle }),
		...(description && { description }),
		...(worksFor && { worksFor: { '@type': 'Organization' as const, name: worksFor } }),
		...(sameAs.length > 0 && { sameAs }),
	}
}

// Article structured data (JSON-LD) - post pages only, not the category/listing view
if (pageContext?.type === 'post') {
	useJsonld(() => {
		const publishedDate = newsForm.date instanceof Date ? newsForm.date : new Date(newsForm.date)

		return {
			'@context': 'https://schema.org',
			'@type': 'Article',
			headline: newsForm.title,
			description: metaDescription.value,
			image: newsForm.topImage?.path ? `${storageUrl}${newsForm.topImage.path}` : `${url.origin}/car-free-1200x630.jpg`,
			datePublished: isNaN(publishedDate.getTime()) ? undefined : publishedDate.toISOString(),
			inLanguage: newsForm.language ? newsForm.language.toLowerCase() : 'pl',
			mainEntityOfPage: {
				'@type': 'WebPage',
				'@id': newsForm.metaCanonical && newsForm.metaCanonical?.trim() !== '' ? newsForm.metaCanonical : `${url.origin}${url.pathname}`,
			},
			author: buildAuthorJsonld(newsForm.author, newsForm.language ? newsForm.language.toLowerCase() : 'pl') ?? {
				'@type': 'Organization',
				name: 'CarFree',
				url: url.origin,
			},
			publisher: {
				'@type': 'Organization',
				name: 'CarFree',
				logo: {
					'@type': 'ImageObject',
					url: `${url.origin}/logo-cf.svg`,
				},
			},
		}
	})
}

const breadcrumbsItems = ref([
	{ title: $t('common.home_page'), href: localePath('/') },
	{ title: $t('common.news'), href: localePath({ name: 'blog' }, localeLang) },
	{ title: newsForm?.title, href: route.fullPath },
])

const computedLatestNews = computed(() => {
	const filteredNews = latestNews.value.filter((news) => news.slug !== segment)
	return filteredNews.length > 3 ? filteredNews.slice(0, 3) : filteredNews
})

definePageMeta({
	middleware: ['news'],
	key: (route) => route.fullPath,
})

const robotsCalculated = useRobotsMeta(() => newsForm.metaRobots)

usePageViewed(isListingPage ? EGTMPageType.NEWS : EGTMPageType.NEWS_POST)
</script>

<style lang="scss" scoped src="./index.scss"></style>
